import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';

interface AlertDetail {
  id: number;
  store_id: number;
  monitor_id: number;
  priority: number;
  event_type: string;
  title: string;
  description: string;
  status: string;
  camera_location: string | null;
  attachments: { s3_key?: string; url?: string; type?: string }[];
  employee_id: number | null;
  monitor_email: string;
  employee_name: string | null;
  notes: { author_type: string; author_name?: string; text: string; timestamp: string }[];
  created_at: string;
  updated_at: string | null;
}

const PRIORITY_TO_SEVERITY: Record<number, string> = { 0: 'critical', 1: 'high', 2: 'medium', 3: 'low' };

const SEVERITY_CONFIG: Record<string, { label: string; badgeClass: string }> = {
  low: { label: 'Low', badgeClass: 'badge badge-neutral' },
  medium: { label: 'Medium', badgeClass: 'badge badge-info' },
  high: { label: 'High', badgeClass: 'badge badge-warning' },
  critical: { label: 'Critical', badgeClass: 'badge badge-critical' },
};

const STATUS_CONFIG: Record<string, { label: string; badgeClass: string }> = {
  open: { label: 'Open', badgeClass: 'badge badge-info' },
  acknowledged: { label: 'Acknowledged', badgeClass: 'badge badge-warning' },
  resolved: { label: 'Resolved', badgeClass: 'badge badge-success' },
  closed: { label: 'Closed', badgeClass: 'badge badge-neutral' },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  theft_shoplifting: 'Theft / Shoplifting', robbery: 'Robbery', safety_hazard: 'Safety Hazard',
  employee_issue: 'Employee Issue', suspicious_activity: 'Suspicious Activity',
  customer_incident: 'Customer Incident', maintenance: 'Maintenance', delivery: 'Delivery',
  cash_handling: 'Cash Handling', shift_change: 'Shift Change', fuel_incident: 'Fuel Incident',
  custom: 'Custom', theft: 'Theft', employee_theft: 'Employee Theft', no_show: 'No Show',
  late_arrival: 'Late Arrival', early_departure: 'Early Departure',
  unauthorized_area: 'Unauthorized Area', safety_violation: 'Safety Violation',
  property_damage: 'Property Damage', maintenance_issue: 'Maintenance Issue',
  inventory: 'Inventory', other: 'Other',
};

function formatEventType(et: string): string {
  return EVENT_TYPE_LABELS[et] || et.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function isImageUrl(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp)/i.test(url);
}

export default function AlertDetailPage() {
  const { alertId } = useParams<{ alertId: string }>();
  const navigate = useNavigate();

  const [alert, setAlert] = useState<AlertDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [noteError, setNoteError] = useState('');

  const fetchAlert = useCallback(async () => {
    try {
      const res = await api.get(`/monitors/alerts/${alertId}`);
      setAlert(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load alert');
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => { fetchAlert(); }, [fetchAlert]);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    setNoteError('');
    try {
      const res = await api.post(`/monitors/alerts/${alertId}/notes`, { text: noteText.trim() });
      setAlert(res.data);
      setNoteText('');
    } catch (err: any) {
      setNoteError(err?.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) return <div style={styles.page}><p style={styles.loadingText}>Loading alert...</p></div>;
  if (error) return (
    <div style={styles.page}>
      <div style={{ padding: 40 }}>
        <div style={styles.errorBanner}>{error}</div>
        <button onClick={() => navigate('/alerts')} style={styles.backBtn}>Back to Alerts</button>
      </div>
    </div>
  );
  if (!alert) return null;

  const severity = PRIORITY_TO_SEVERITY[alert.priority] || 'low';
  const sevConfig = SEVERITY_CONFIG[severity];
  const statConfig = STATUS_CONFIG[alert.status] || STATUS_CONFIG.open;

  const images = alert.attachments.filter(a => a.url && isImageUrl(a.url || a.s3_key || ''));
  const videos = alert.attachments.filter(a => a.url && !isImageUrl(a.url || a.s3_key || ''));

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.topRow}>
          <button onClick={() => navigate('/alerts')} style={styles.backBtn}>
            &larr; Back to Alerts
          </button>
          <button onClick={() => navigate(`/alerts/${alertId}/edit`)} style={styles.editBtn}>
            Edit Alert
          </button>
        </div>

        {/* Alert info card */}
        <div style={styles.card}>
          <div style={styles.infoRow}>
            <span className={sevConfig.badgeClass}>
              {sevConfig.label}
            </span>
            <span className={statConfig.badgeClass}>
              {statConfig.label}
            </span>
            <span className="badge badge-neutral">{formatEventType(alert.event_type)}</span>
            <span style={styles.timestamp}>{formatTimestamp(alert.created_at)}</span>
          </div>

          <h2 style={styles.alertTitle}>{alert.title}</h2>
          <p style={styles.alertDescription}>{alert.description}</p>

          <div style={styles.metaGrid}>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Monitor</span>
              <span style={styles.metaValue}>{alert.monitor_email}</span>
            </div>
            {alert.camera_location && (
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Camera</span>
                <span style={styles.metaValue}>{alert.camera_location}</span>
              </div>
            )}
            {alert.employee_name && (
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Employee</span>
                <span style={styles.metaValue}>{alert.employee_name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Attachments */}
        {alert.attachments.length > 0 && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Attachments</h3>
            {images.length > 0 && (
              <div style={styles.imageGrid}>
                {images.map((att, i) => (
                  <a key={i} href={att.url} target="_blank" rel="noopener noreferrer">
                    <img src={att.url} alt={`Attachment ${i + 1}`} style={styles.attachmentImg} />
                  </a>
                ))}
              </div>
            )}
            {videos.length > 0 && (
              <div style={{ marginTop: images.length > 0 ? 12 : 0 }}>
                {videos.map((att, i) => (
                  <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" style={styles.videoLink}>
                    Video {i + 1}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Notes</h3>
          {(alert.notes || []).length === 0 && (
            <p style={styles.emptyText}>No notes yet.</p>
          )}
          <div style={styles.notesList}>
            {(alert.notes || []).map((note, i) => (
              <div key={i} style={styles.note}>
                <div style={styles.noteHeader}>
                  <span style={styles.noteAuthor}>
                    {note.author_name || note.author_type}
                  </span>
                  <span style={styles.noteTime}>{formatTimestamp(note.timestamp)}</span>
                </div>
                <p style={styles.noteText}>{note.text}</p>
              </div>
            ))}
          </div>

          {/* Add note form */}
          <div style={styles.noteForm}>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Add a note..."
              rows={3}
              style={styles.noteTextarea}
            />
            {noteError && <p style={styles.noteErrorText}>{noteError}</p>}
            <button
              onClick={handleAddNote}
              disabled={addingNote || !noteText.trim()}
              style={{
                ...styles.noteSubmitBtn,
                opacity: addingNote || !noteText.trim() ? 0.5 : 1,
                cursor: addingNote || !noteText.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {addingNote ? 'Adding...' : 'Add Note'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {},
  loadingText: {
    padding: 40,
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-body)',
  },
  main: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '32px 24px',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    padding: 0,
  },
  editBtn: {
    padding: '8px 20px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--accent)',
    background: 'transparent',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
  },
  card: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    padding: 24,
    marginBottom: 20,
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  timestamp: {
    marginLeft: 'auto',
    fontSize: 'var(--text-xs)',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-tertiary)',
  },
  alertTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-xl)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: '0 0 10px',
  },
  alertDescription: {
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap' as const,
  },
  metaGrid: {
    display: 'flex',
    gap: 24,
    marginTop: 20,
    flexWrap: 'wrap' as const,
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  metaLabel: {
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  metaValue: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
  },
  cardTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 16px',
  },
  imageGrid: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap' as const,
  },
  attachmentImg: {
    width: 200,
    height: 150,
    objectFit: 'cover' as const,
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    cursor: 'pointer',
  },
  videoLink: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: 'var(--radius-md)',
    background: 'var(--accent-muted)',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    textDecoration: 'none',
    marginRight: 8,
  },
  emptyText: {
    color: 'var(--text-tertiary)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    margin: 0,
  },
  notesList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  note: {
    padding: '14px 16px',
    background: 'var(--bg-surface-raised)',
    borderRadius: 'var(--radius-md)',
  },
  noteHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  noteAuthor: {
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    textTransform: 'capitalize' as const,
  },
  noteTime: {
    fontSize: 'var(--text-xs)',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-tertiary)',
  },
  noteText: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.5,
  },
  noteForm: {
    marginTop: 20,
    borderTop: '1px solid var(--border-subtle)',
    paddingTop: 16,
  },
  noteTextarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    outline: 'none',
  },
  noteErrorText: {
    color: 'var(--status-critical)',
    fontSize: 'var(--text-xs)',
    fontFamily: 'var(--font-body)',
    margin: '6px 0 0',
  },
  noteSubmitBtn: {
    marginTop: 10,
    padding: '8px 20px',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '14px 18px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    marginBottom: 16,
  },
};
